import { Injectable, OnDestroy } from '@angular/core';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { BehaviorSubject, Subject, Subscription, interval, timer, Observable, lastValueFrom } from 'rxjs';
import { UserInfoService } from './user-info.service';
import { SessionService } from './session.service';
import { ApiService } from './../../../../goldstar-share/src/app/api-data/ng-openapi-gen-next/services';
import { WebSocketTicketResponse } from '../../../../goldstar-share/src/app/api-data/ng-openapi-gen-next/models';

export interface WebSocketEnvelope {
	action: string;
	message?: any;
}

export enum WebSocketStatus {
	ATTEMPTING = 'ATTEMPTING',
	CONNECTED = 'CONNECTED',
	DISCONNECTED = 'DISCONNECTED',
	FAILED = 'FAILED',
	RECONNECTING = 'RECONNECTING',
	ERROR = 'ERROR',
}

type ConnectionState = 'disconnected' | 'connecting' | 'connected';

@Injectable({ providedIn: 'root' })
export class WebSocketService implements OnDestroy {
	private statusSubject = new BehaviorSubject<WebSocketStatus>(WebSocketStatus.DISCONNECTED);
	public readonly status$: Observable<WebSocketStatus> = this.statusSubject.asObservable();
	
	private static ws: WebSocket | null = null;
	private isManuallyClosed: boolean = false;
	private heartbeatInterval: ReturnType<typeof setInterval> | undefined;
	private maxReconnectDelay = 10000; // 10 seconds
	private reconnectAttempts: number = 0;
	private maxReconnectAttempts: number = 5;
	private messageTimeoutMap: Map<string, ReturnType<typeof setTimeout>> = new Map();
	private connectionLifeCycle: ReturnType<typeof setTimeout> | undefined;
	private awsWebSocketConnectionTimeout: number = 1.9 * 3600 * 1000; // 1.9 hours in milliseconds (AWS timeout is 2 hours)

	constructor(private userInfoService: UserInfoService, private sessionService: SessionService, private apiService: ApiService) {}

	public connect(): void {
		this.isManuallyClosed = false;

		if(WebSocketService.ws && WebSocketService.ws.readyState === WebSocket.OPEN) {
			console.log('[WS] WebSocket is already connected.');
			return;
		}
		
		try {
			const url = `${this.sessionService.webSocketUrl}?ticket=${this.sessionService.webSocketTicket}&sessionGUID=${this.sessionService.sessionGUID}`;
			console.log('[WS] Connecting to WebSocket at: ', url);
			WebSocketService.ws = new WebSocket(url);
			this.attachHandlers();
		} catch (error) {
			console.error('WebSocket connection error:', error);
			this.scheduleReconnect();
		}
	}

	public disconnect(): void {
		this.isManuallyClosed = true;
		this.statusSubject.next(WebSocketStatus.DISCONNECTED);
		this.stopHeartbeat();
		WebSocketService.ws?.close();
		this.messageTimeoutMap.forEach((timeout) => {
			clearTimeout(timeout);
		});
		this.messageTimeoutMap.clear();
	}

	public async send(message: WebSocketEnvelope): Promise<void> {
		if(WebSocketService.ws?.readyState === WebSocket.OPEN) {
			console.log('[WS] Sending message: ',message);
			WebSocketService.ws?.send(JSON.stringify({
				...message,
				sessionGUID: this.sessionService.sessionGUID,
			}));

			if(message.action === 'ping') {
				this.setMessageTimeout('ping', 5000, true);
			}

			if(message.action === 'notification') {
				this.setMessageTimeout('notification', 30000, true);
			}

		} else {
			console.warn('[WS] Cannot send message, WebSocket is not open. Current state: ', WebSocketService.ws?.readyState);
		}
	}

	ngOnDestroy(): void {
		this.statusSubject.next(WebSocketStatus.DISCONNECTED);
		this.disconnect();
	}

	get currentStatus(): WebSocketStatus {
		return this.statusSubject.value;
	}


	private attachHandlers(): void {
		WebSocketService.ws!.onopen = () => {
			console.log('[WS] Connected');
			this.statusSubject.next(WebSocketStatus.CONNECTED);
			this.reconnectAttempts = 0;
			this.startHeartbeat();
		};

		WebSocketService.ws!.onmessage = (event) => {
			let data;
			try {
				//console.log('[WS] debugging ', event);
				data = JSON.parse(event.data);
				console.log('[WS] Message data: ', data);
				this.clearMessageTimeout(data.action);
			} catch {
				console.warn('[WS] Received non-JSON message: ', event.data);
			}

			if(data.action === 'pong') {
				// example of handling a response when we want to do something specific with it, like logging or updating a status
				return;
			}
		};

		WebSocketService.ws!.onclose = (event) => {
			console.log('[WS] Disconnected', event);
			this.statusSubject.next(WebSocketStatus.DISCONNECTED);
			if (!this.isManuallyClosed) {
				this.scheduleReconnect();
			}
		};

		WebSocketService.ws!.onerror = (error) => {
			console.error('[WS] Error: ', error);
			this.statusSubject.next(WebSocketStatus.ERROR);
		};

		clearTimeout(this.connectionLifeCycle);
			this.connectionLifeCycle = setTimeout(() => {
			console.warn(`[WS] Approaching AWS WebSocket connection timeout, reconnecting...`);
			this.statusSubject.next(WebSocketStatus.RECONNECTING);
			this.resetConnection();
		}, this.awsWebSocketConnectionTimeout);
	}

	private async scheduleReconnect(): Promise<void> {
		if(this.reconnectAttempts >= this.maxReconnectAttempts) {
			console.log('[WS] Max reconnect attempts reached. Giving up.');
			this.statusSubject.next(WebSocketStatus.FAILED);
		} else {
			this.statusSubject.next(WebSocketStatus.RECONNECTING);
			const delay = Math.min(1000 * 2 ** this.reconnectAttempts, this.maxReconnectDelay);
			this.reconnectAttempts++;
			await this.getNewTicket(); // Get a new ticket before reconnecting
			console.log(`[WS] Attempting to reconnect in ${delay / 1000} seconds... (Attempt ${this.reconnectAttempts})`);
			setTimeout(() => this.connect(), delay);
		}
	}

	private startHeartbeat(): void {
		this.heartbeatInterval = setInterval(() => {
			if (WebSocketService.ws?.readyState === WebSocket.OPEN) {
				console.log('[WS] Sending heartbeat ping');
				WebSocketService.ws.send(JSON.stringify({ action: 'ping', message: {} }));
			}
		}, 5 * 60 * 1000); // 5 minutes in milliseconds
	}

	private stopHeartbeat(): void {
		clearInterval(this.heartbeatInterval);
	}

	private async getNewTicket(): Promise<void> {
		const internalUserGUID = await this.userInfoService.getCurrentLoggedInUser().then(user => user.internalUserGUID ?? '');
		if(!internalUserGUID) {
			return Promise.reject('No internalUserGUID found for current user');
		}
		const ticketResponse: { isSuccess?: boolean | undefined, message?: string | undefined, data?: WebSocketTicketResponse | undefined } = await lastValueFrom(this.apiService.getNewWebSocketTicket({ body: { internalUserGUID } }));
		if(ticketResponse.isSuccess && ticketResponse.data && ticketResponse.data.webSocketTicket) {
			this.sessionService.webSocketTicket = ticketResponse.data.webSocketTicket;
		} else {
			return Promise.reject('Failed to get new WebSocket ticket');
		}
	}

	private setMessageTimeout(messageType: string, timeoutDuration: number, forceReconnect: boolean, onTimeout?: () => void): void {
		this.clearMessageTimeout(messageType);
		const newTimeout = setTimeout(onTimeout ?? (() => {
			console.warn(`[WS] Message of type "${messageType}" timed out after ${timeoutDuration / 1000} seconds`);
			if (forceReconnect) {
				console.warn(`[WS] Forcing reconnection`);
				this.resetConnection();
			}
		}), timeoutDuration ?? 30000); // Default to 30 seconds if no duration is provided
		this.messageTimeoutMap.set(messageType, newTimeout);
	}

	private clearMessageTimeout(messageType: string): void {
		const timeout = this.messageTimeoutMap.get(messageType);
		if (timeout) {
			clearTimeout(timeout);
			this.messageTimeoutMap.delete(messageType);
		}
	}

	private resetConnection(): void {
		this.reconnectAttempts = 0;
		this.disconnect();
		this.scheduleReconnect();
	}
}
